import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Page } from "@/components/Layout";
import { useAuth } from "@/lib/auth";
import { buyPackage } from "@/lib/buyPackage";
import { useCurrency } from "@/lib/currency";
import { ArrowRight, Check, Star } from "@phosphor-icons/react";

const programmes = [
  {
    name: "The First Mile",
    tagline: "Starting out",
    price: 8800,
    sessions: 4,
    expiry: "10-week expiry",
    features: [
      "4 mentorship sessions",
      "Career & pathway discovery",
      "1 application review",
      "Action plan + resource pack",
    ],
  },
  {
    name: "The Crossing",
    tagline: "Mid-journey",
    price: 17000,
    sessions: 8,
    expiry: "16-week expiry",
    features: [
      "8 structured mentorship sessions",
      "Application construction & review",
      "2 mock interviews",
      "Email support between sessions",
    ],
    featured: true,
  },
  {
    name: "The Horizon",
    tagline: "Going global",
    price: 24000,
    sessions: 12,
    expiry: "12-month expiry",
    features: [
      "12 mentorship sessions",
      "Direct WhatsApp access throughout",
      "3 emergency calls when life gets loud",
      "End-to-end study abroad support",
    ],
  },
];

export default function Packages() {
  const { user } = useAuth();
  const { format } = useCurrency();
  const navigate = useNavigate();
  const [buying, setBuying] = useState("");

  const handleBuy = async (p, testId) => {
    setBuying(testId);
    await buyPackage({ id: `pkg-${testId}`, name: p.name, price: p.price, user, navigate });
    setBuying("");
  };

  return (
    <Page>
      <section className="md-section md-page-hero" data-testid="packages-hero">
        <div className="md-container">
          <div className="md-label-eyebrow mb-4">Forward · Packages</div>
          <h1 className="text-5xl md:text-6xl font-serif mb-6">Three programmes for three distances</h1>
          <p className="text-lg max-w-2xl mx-auto" style={{ color: "rgba(255,255,255,0.92)" }}>Or book single sessions, your call.</p>
        </div>
      </section>
      <section className="md-section">
        <div className="md-container">
          <div className="grid md:grid-cols-3 gap-6">
            {programmes.map((p) => {
              const testId = p.name.toLowerCase().replace(/\s+/g, "-");
              return (
                <div
                  key={p.name}
                  className={`md-card md-card-hover relative ${p.featured ? "ring-2" : ""}`}
                  style={p.featured ? { boxShadow: "0 0 0 2px var(--md-primary)" } : {}}
                  data-testid={`package-card-${testId}`}
                >
                  {p.featured && (
                    <span
                      className="absolute -top-3 left-6 inline-flex items-center gap-1 text-[10px] font-bold tracking-widest uppercase px-3 py-1 rounded-full"
                      style={{ background: "var(--md-primary)", color: "#fff", letterSpacing: "0.12em" }}
                      data-testid="package-recommended-badge"
                    >
                      <Star size={10} weight="fill" /> Recommended
                    </span>
                  )}
                  <div className="md-label-eyebrow">{p.tagline}</div>
                  <h3 className="text-3xl font-serif mt-2">{p.name}</h3>
                  <p className="text-3xl font-serif mt-4" style={{ color: "var(--md-primary)" }} data-testid={`package-price-${testId}`}>
                    {format(p.price)}
                  </p>
                  <p className="text-xs mt-1" style={{ color: "var(--md-text-muted)" }} data-testid={`package-meta-${testId}`}>
                    {p.sessions} sessions · {p.expiry}
                  </p>
                  <ul className="mt-6 space-y-3">
                    {p.features.map((f) => (
                      <li key={f} className="flex gap-2 text-sm">
                        <Check size={16} weight="bold" className="mt-0.5 shrink-0" style={{ color: "var(--md-accent)" }} /> {f}
                      </li>
                    ))}
                  </ul>
                  <button
                    type="button"
                    onClick={() => handleBuy(p, testId)}
                    disabled={buying === testId}
                    className={`mt-7 w-full justify-center ${p.featured ? "btn-primary" : "btn-outline"}`}
                    data-testid={`package-cta-${testId}`}
                  >
                    {buying === testId ? "Opening checkout…" : (<>Buy Now <ArrowRight size={14} weight="bold" /></>)}
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      </section>
    </Page>
  );
}
